import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  ANIMATION_FINISHED,
  ANIMATION_STOPPED,
  INITIALIZED,
} from '../TextParticles.types';
import type {
  WorkerMessage,
  WorkerMessageTypes,
  Message,
  PLAY,
  EffectConfiguration,
} from '../TextParticles.types';
import type { getMainThreadMessages } from '../worker/messages';
import { isImageTransferred } from '../utils/image-utils';
import { TARGET_TOTAL_PARTICLE_COUNT } from '../../constants';
import { getDevicePixelRatio } from '../utils/canvas-utils';

export type UseParticleRendererProps = {
  image: ImageBitmap | null;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onAnimationEnd: () => void;
  componentId: string;
  worker: Worker | null;
  messages: ReturnType<typeof getMainThreadMessages>;
  onInitialized: () => void;
  onAnimationStopped: () => void;
  effectConfiguration: EffectConfiguration | null;
};

export const useParticleRenderer = ({
  effectConfiguration,
  canvasRef,
  image,
  onAnimationEnd,
  componentId,
  worker,
  messages,
  onInitialized,
  onAnimationStopped,
}: UseParticleRendererProps) => {
  const devicePixelRatio = useMemo(() => getDevicePixelRatio(), []);
  const [workerState, setWorkerState] = useState<'idle' | 'pending' | 'ready'>(
    'idle',
  );
  const currentAnimationPromiseRef = useRef<Promise<void> | null>(null);
  const currentAnimationResolverRef = useRef<(() => void) | null>(null);
  const pendingPlayMessageRef = useRef<Message<typeof PLAY> | null>(null);

  const playRef = useRef<() => Promise<void>>(async () => {});
  const stopRef = useRef<() => void>(() => {});
  const pauseRef = useRef<() => void>(() => {});

  const handleAnimationStopped = useCallback(() => {
    if (currentAnimationResolverRef.current) {
      currentAnimationResolverRef.current();
      currentAnimationResolverRef.current = null;
    }
    currentAnimationPromiseRef.current = null;
    pendingPlayMessageRef.current = null;
    onAnimationStopped();
  }, [onAnimationStopped]);

  const messageHandler = useCallback(
    (message: MessageEvent<WorkerMessage>) => {
      const { data } = message;
      const workerMessageHandler: Record<WorkerMessageTypes, () => void> = {
        [INITIALIZED]: () => {
          onInitialized();
          setWorkerState('ready');
        },
        [ANIMATION_STOPPED]: () => {
          handleAnimationStopped();
        },
        [ANIMATION_FINISHED]: () => {
          onAnimationEnd();
          if (currentAnimationResolverRef.current) {
            currentAnimationResolverRef.current();
            currentAnimationResolverRef.current = null;
          }
          currentAnimationPromiseRef.current = null;
        },
      };

      if (componentId === data.componentId) {
        workerMessageHandler[data.type]?.();
      }
    },
    [componentId, onAnimationEnd, onInitialized, handleAnimationStopped],
  );

  useEffect(() => {
    if (!worker) {
      return;
    }

    worker.addEventListener('message', messageHandler);

    return () => {
      worker.removeEventListener('message', messageHandler);
    };
  }, [messageHandler, worker]);

  useEffect(() => {
    if (!worker || workerState !== 'ready') {
      return;
    }

    if (pendingPlayMessageRef.current) {
      worker.postMessage(pendingPlayMessageRef.current);
      pendingPlayMessageRef.current = null;
    }
  }, [worker, workerState]);

  useEffect(() => {
    if (!worker || workerState !== 'ready' || !effectConfiguration) {
      return;
    }

    worker.postMessage(
      messages.getUpdateRendererPropsMessage(effectConfiguration),
    );
  }, [worker, workerState, messages, effectConfiguration]);
  useEffect(() => {
    if (
      !canvasRef.current ||
      !worker ||
      workerState === 'ready' ||
      workerState === 'pending' ||
      !image ||
      isImageTransferred(image) ||
      !effectConfiguration
    ) {
      return;
    }

    setWorkerState('pending');
    const transferredCanvas = canvasRef.current.transferControlToOffscreen();

    worker.postMessage(
      messages.getInitializeMessage({
        imageBitmap: image,
        canvas: transferredCanvas,
        effectConfiguration,
        devicePixelRatio,
        targetParticleCount: TARGET_TOTAL_PARTICLE_COUNT,
      }),
      [image, transferredCanvas],
    );
  }, [
    canvasRef,
    workerState,
    image,
    worker,
    componentId,
    messages,
    effectConfiguration,
    devicePixelRatio,
  ]);

  useEffect(() => {
    if (
      !worker ||
      workerState !== 'ready' ||
      !image ||
      isImageTransferred(image)
    ) {
      return;
    }

    worker.postMessage(messages.getUpdateImageMessage({ imageBitmap: image }), [
      image,
    ]);
  }, [componentId, image, workerState, worker, messages]);

  useEffect(() => {
    playRef.current = (): Promise<void> => {
      if (currentAnimationPromiseRef.current) {
        return currentAnimationPromiseRef.current;
      }

      const animationPromise = new Promise<void>(resolve => {
        currentAnimationResolverRef.current = resolve;

        const playMessage = messages.getPlayMessage();

        if (worker && workerState === 'ready') {
          worker.postMessage(playMessage);
        } else {
          pendingPlayMessageRef.current = playMessage;
        }
      });

      currentAnimationPromiseRef.current = animationPromise;

      animationPromise.finally(() => {
        if (currentAnimationPromiseRef.current === animationPromise) {
          currentAnimationPromiseRef.current = null;
        }
      });

      return animationPromise;
    };
  }, [worker, messages, workerState]);

  useEffect(() => {
    stopRef.current = () => {
      pendingPlayMessageRef.current = null;

      if (worker && workerState === 'ready') {
        worker.postMessage(messages.getStopMessage());
      }

      if (currentAnimationResolverRef.current) {
        currentAnimationResolverRef.current();
        currentAnimationResolverRef.current = null;
      }
      currentAnimationPromiseRef.current = null;
    };

    pauseRef.current = () => {
      pendingPlayMessageRef.current = null;

      if (worker && workerState === 'ready') {
        worker.postMessage(messages.getPauseMessage());
      }

      if (currentAnimationResolverRef.current) {
        currentAnimationResolverRef.current();
        currentAnimationResolverRef.current = null;
      }
      currentAnimationPromiseRef.current = null;
    };
  }, [worker, workerState, messages]);

  const play = useCallback(async () => playRef.current(), []);

  const stop = useCallback(() => stopRef.current(), []);

  const pause = useCallback(() => pauseRef.current(), []);

  return {
    play,
    stop,
    pause,
  };
};
